import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";
import { resolveAssetUrl } from "@/lib/api";
import { Search, User, ShoppingBag, Menu, X, LogOut } from "lucide-react";

const Header = () => {
  const { itemCount, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <div
        className="bg-white text-black text-center text-xs font-bold tracking-widest py-2 px-4 uppercase"
        data-testid="announcement-bar"
      >
        Forged in Faith. Refined by Discipline. Free shipping on orders over
        $100
      </div>

      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link
            to="/"
            className="flex items-center space-x-3"
            data-testid="brand-logo-link"
          >
            <img
              src={resolveAssetUrl(BRAND.LOGO)}
              alt="Faith Athletic Clothing"
              className="h-12 w-12 object-contain"
              data-testid="brand-logo-img"
            />
            <span className="font-heading text-xl font-bold tracking-widest border-l pl-3 border-zinc-700 hidden sm:inline-block">
              FAITH{" "}
              <span className="text-[10px] block tracking-[0.2em] text-zinc-400">
                ATHLETIC CLOTHING
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex space-x-8 font-bold text-xs tracking-widest uppercase">
            <Link
              to="/welcome"
              className="hover:text-zinc-400 transition-colors"
              data-testid="nav-welcome"
            >
              Welcome
            </Link>
            <a
              href="/#shop"
              className="hover:text-zinc-400 transition-colors"
              data-testid="nav-shop"
            >
              Shop All
            </a>
            <Link
              to="/lady-starzz"
              className="hover:text-zinc-400 transition-colors"
              data-testid="nav-lady-starzz"
            >
              Lady Starzz
            </Link>
            <a
              href="/#culture"
              className="hover:text-zinc-400 transition-colors"
              data-testid="nav-culture"
            >
              Our Culture
            </a>
            <a
              href="/#about"
              className="hover:text-zinc-400 transition-colors"
              data-testid="nav-about"
            >
              Our Legacy
            </a>
            {user?.role === "admin" && (
              <Link
                to="/admin"
                className="hover:text-zinc-400 transition-colors text-zinc-500"
                data-testid="nav-admin"
              >
                Admin
              </Link>
            )}
          </nav>

          <div className="flex items-center space-x-6 text-sm">
            <button
              className="hidden sm:inline hover:text-zinc-400"
              aria-label="Search"
              data-testid="header-search-btn"
            >
              <Search size={16} />
            </button>
            {user ? (
              <button
                onClick={() => logout()}
                className="hidden sm:inline hover:text-zinc-400"
                aria-label="Logout"
                title={`Logout (${user.email})`}
                data-testid="header-logout-btn"
              >
                <LogOut size={16} />
              </button>
            ) : (
              <Link
                to="/admin/login"
                className="hidden sm:inline hover:text-zinc-400"
                aria-label="Account"
                data-testid="header-account-btn"
              >
                <User size={16} />
              </Link>
            )}
            <button
              className="hover:text-zinc-400 relative"
              onClick={() => setOpen(true)}
              aria-label="Open cart"
              data-testid="header-cart-btn"
            >
              <ShoppingBag size={18} />
              {itemCount > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-red-600 text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold"
                  data-testid="cart-count-badge"
                >
                  {itemCount}
                </span>
              )}
            </button>
            <button
              className="md:hidden hover:text-zinc-400"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            className="md:hidden border-t border-zinc-800 bg-black/95"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col px-6 py-4 space-y-3 font-bold text-xs tracking-widest uppercase">
              <Link
                to="/welcome"
                onClick={() => setMobileOpen(false)}
                className="hover:text-zinc-400"
              >
                Welcome
              </Link>
              <a
                href="/#shop"
                onClick={() => setMobileOpen(false)}
                className="hover:text-zinc-400"
              >
                Shop All
              </a>
              <Link
                to="/lady-starzz"
                onClick={() => setMobileOpen(false)}
                className="hover:text-zinc-400"
              >
                Lady Starzz
              </Link>
              <a
                href="/#culture"
                onClick={() => setMobileOpen(false)}
                className="hover:text-zinc-400"
              >
                Our Culture
              </a>
              <a
                href="/#about"
                onClick={() => setMobileOpen(false)}
                className="hover:text-zinc-400"
              >
                Our Legacy
              </a>
              {user?.role === "admin" ? (
                <>
                  <Link
                    to="/admin"
                    onClick={() => setMobileOpen(false)}
                    className="text-zinc-500"
                  >
                    Admin Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      setMobileOpen(false);
                      logout();
                    }}
                    className="text-left text-zinc-500"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/admin/login"
                  onClick={() => setMobileOpen(false)}
                  className="text-zinc-500"
                >
                  Admin Login
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
